import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Copy, FileDown, Printer, RotateCcw, Share2 } from 'lucide-react'
import {
  FT_PER_M,
  LIMITS,
  MATERIALS,
  buildResultsText,
  calculateDormer,
  round,
  validateInputs,
} from '@/lib/calculator'
import type { FieldErrors, FieldKey, RawInputs, UnitSystem } from '@/lib/calculator'
import ResultsPanel from './ResultsPanel'
import SponsoredSlot from '@/components/SponsoredSlot'
import { printResults } from '@/lib/print'
import { saveResultsPdf } from '@/lib/pdf'

const DEFAULTS: Record<UnitSystem, RawInputs> = {
  imperial: { width: '10', depth: '8', height: '4', pitch: '4' },
  metric: { width: '3', depth: '2.4', height: '1.2', pitch: '4' },
}
const DEFAULT_MATERIAL = 'fiber-cement'

interface FieldDef {
  key: FieldKey
  label: string
  hint: string
}

const FIELDS: FieldDef[] = [
  { key: 'width', label: 'Width', hint: 'Left to right across the roof' },
  { key: 'depth', label: 'Depth', hint: 'Front face back to the main roof' },
  { key: 'height', label: 'Wall height', hint: 'Front wall, floor to roof edge' },
  { key: 'pitch', label: 'Roof pitch', hint: 'Rise in inches per 12 in of run' },
]

function readInitial(params: URLSearchParams): { unit: UnitSystem; raw: RawInputs; material: string } {
  const unit: UnitSystem = params.get('u') === 'm' ? 'metric' : 'imperial'
  const base = DEFAULTS[unit]
  const raw: RawInputs = {
    width: params.get('w') ?? base.width,
    depth: params.get('d') ?? base.depth,
    height: params.get('h') ?? base.height,
    pitch: params.get('p') ?? base.pitch,
  }
  const m = params.get('m')
  const material = MATERIALS.some(x => x.id === m) ? (m as string) : DEFAULT_MATERIAL
  return { unit, raw, material }
}

function CalculatorTool() {
  const [params] = useSearchParams()
  const initial = useMemo(() => readInitial(params), [params])
  const [unit, setUnit] = useState<UnitSystem>(initial.unit)
  const [raw, setRaw] = useState<RawInputs>(initial.raw)
  const [materialId, setMaterialId] = useState<string>(initial.material)
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({})

  const { values, errors } = useMemo(() => validateInputs(raw, unit, materialId), [raw, unit, materialId])
  const result = useMemo(() => (values ? calculateDormer(values) : null), [values])

  const showError = (key: FieldKey): string | undefined => (touched[key] ? errors[key] : undefined)

  const updateField = (key: FieldKey, value: string) => {
    setRaw(prev => ({ ...prev, [key]: value }))
    setTouched(prev => ({ ...prev, [key]: true }))
  }

  const switchUnit = (next: UnitSystem) => {
    if (next === unit) return
    const convert = (text: string, key: FieldKey): string => {
      const n = Number(text)
      if (text.trim() === '' || !Number.isFinite(n) || key === 'pitch') return text
      const converted = next === 'metric' ? n / FT_PER_M : n * FT_PER_M
      return String(round(converted, 2))
    }
    setRaw(prev => ({
      width: convert(prev.width, 'width'),
      depth: convert(prev.depth, 'depth'),
      height: convert(prev.height, 'height'),
      pitch: prev.pitch,
    }))
    setUnit(next)
  }

  const reset = () => {
    setUnit('imperial')
    setRaw(DEFAULTS.imperial)
    setMaterialId(DEFAULT_MATERIAL)
    setTouched({})
    toast.success('Calculator reset')
  }

  const touchAll = () => setTouched({ width: true, depth: true, height: true, pitch: true })

  const copyResults = async () => {
    if (!values || !result) {
      touchAll()
      toast.error('Fix the highlighted fields first.')
      return
    }
    try {
      await navigator.clipboard.writeText(buildResultsText(values, result, unit))
      toast.success('Results copied to clipboard')
    } catch {
      toast.error('Copy failed. Select the results and copy manually.')
    }
  }

  const shareResults = async () => {
    if (!values || !result) {
      touchAll()
      toast.error('Fix the highlighted fields first.')
      return
    }
    const q = new URLSearchParams({
      w: raw.width,
      d: raw.depth,
      h: raw.height,
      p: raw.pitch,
      m: materialId,
      u: unit === 'metric' ? 'm' : 'i',
    })
    const url = `${window.location.origin}/calculator?${q.toString()}`
    const text = `My dormer estimate: about ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(result.total)}.`
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'Dormer Calculator estimate', text, url })
        return
      }
      await navigator.clipboard.writeText(`${text} ${url}`)
      toast.success('Share link copied to clipboard')
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      toast.error('Sharing failed. Try Copy Results instead.')
    }
  }

  const [savingPdf, setSavingPdf] = useState(false)

  const savePdf = async () => {
    if (!values || !result) {
      touchAll()
      toast.error('Fix the highlighted fields first.')
      return
    }
    setSavingPdf(true)
    const id = toast.loading('Preparing your PDF...')
    try {
      await saveResultsPdf(values, result, unit)
      toast.success('PDF saved to your downloads', { id })
    } catch {
      toast.error('Sorry, the PDF could not be created. Please try again.', { id })
    } finally {
      setSavingPdf(false)
    }
  }

  const printNow = () => {
    if (!values || !result) {
      touchAll()
      toast.error('Fix the highlighted fields first.')
      return
    }
    void printResults()
  }

  const lenUnit = unit === 'metric' ? 'm' : 'ft'
  const lengthRange = (key: FieldKey) => {
    const { min, max } = LIMITS[key]
    return unit === 'metric'
      ? `${round(min / FT_PER_M, 1)} to ${round(max / FT_PER_M, 1)} m`
      : `${min} to ${max} ft`
  }
  const material = MATERIALS.find(m => m.id === materialId)

  return (
    <div className="calc-grid grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start">
      <section
        aria-labelledby="inputs-heading"
        data-print="hide"
        className="card p-5 sm:p-6 lg:sticky lg:top-24"
      >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 id="inputs-heading" className="text-xl">
            Your dormer
          </h2>
          <div role="group" aria-label="Unit system" className="inline-flex rounded-lg border-2 border-steel-200 p-0.5">
            {(['imperial', 'metric'] as UnitSystem[]).map(u => (
              <button
                key={u}
                type="button"
                aria-pressed={unit === u}
                onClick={() => switchUnit(u)}
                className={`min-h-[40px] rounded-md px-3 text-sm font-bold transition-colors ${
                  unit === u ? 'bg-ink text-white' : 'text-steel-600 hover:bg-steel-100'
                }`}
              >
                {u === 'imperial' ? 'Feet' : 'Metres'}
              </button>
            ))}
          </div>
        </div>

        <form
          noValidate
          onSubmit={e => {
            e.preventDefault()
            touchAll()
          }}
          className="space-y-4"
        >
          {FIELDS.map(f => {
            const err = showError(f.key)
            const id = `field-${f.key}`
            const suffix = f.key === 'pitch' ? ': 12' : lenUnit
            const range = f.key === 'pitch' ? `${LIMITS.pitch.min} to ${LIMITS.pitch.max}` : lengthRange(f.key)
            return (
              <div key={f.key}>
                <label htmlFor={id} className="mb-1 block text-sm font-bold text-ink">
                  {f.label} <span className="font-normal text-steel-500">({suffix})</span>
                </label>
                <input
                  id={id}
                  type="number"
                  inputMode="decimal"
                  step="any"
                  className="field-input"
                  value={raw[f.key]}
                  onChange={e => updateField(f.key, e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, [f.key]: true }))}
                  aria-invalid={err ? 'true' : 'false'}
                  aria-describedby={`${id}-hint`}
                />
                <p
                  id={`${id}-hint`}
                  role={err ? 'alert' : undefined}
                  className={`mt-1 text-sm ${err ? 'font-semibold text-danger' : 'text-steel-500'}`}
                >
                  {err ?? `${f.hint}. Range: ${range}.`}
                </p>
              </div>
            )
          })}

          <div>
            <label htmlFor="field-material" className="mb-1 block text-sm font-bold text-ink">
              Material type
            </label>
            <select
              id="field-material"
              className="field-input"
              value={materialId}
              onChange={e => setMaterialId(e.target.value)}
              aria-describedby="field-material-hint"
            >
              {MATERIALS.map(m => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <p id="field-material-hint" className="mt-1 text-sm text-steel-500">
              {material?.description}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
            <button type="button" className="btn btn-primary" onClick={copyResults}>
              <Copy className="h-4 w-4" aria-hidden="true" /> Copy Results
            </button>
            <button type="button" className="btn btn-dark" onClick={shareResults}>
              <Share2 className="h-4 w-4" aria-hidden="true" /> Share Results
            </button>
            <button type="button" className="btn btn-outline" onClick={() => void savePdf()} disabled={savingPdf}>
              <FileDown className="h-4 w-4" aria-hidden="true" /> {savingPdf ? 'Saving...' : 'Save as PDF'}
            </button>
            <button type="button" className="btn btn-outline" onClick={printNow}>
              <Printer className="h-4 w-4" aria-hidden="true" /> Print Results
            </button>
            <button type="button" className="btn btn-outline" onClick={reset}>
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
            </button>
          </div>
        </form>
      </section>

      {/* Shown only on the printed sheet. */}
      <div data-print="keep" className="hidden print:block print:col-span-2">
        <h1 className="text-2xl text-ink">Dormer estimate</h1>
        <p className="mt-1 text-sm text-steel-600">
          Generated at dormercalculator.com on{' '}
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
        <p className="mt-3 text-sm text-ink">
          {FIELDS.map(f => `${f.label}: ${raw[f.key] || '-'} ${f.key === 'pitch' ? ': 12' : lenUnit}`).join('  |  ')}
        </p>
        <p className="mt-1 text-sm text-ink">Finish: {material?.label}</p>
        <hr className="my-4 border-steel-300" />
      </div>

      <ResultsPanel result={result} unit={unit} errors={errors as FieldErrors} />

      <div data-print="hide">
        <SponsoredSlot />
      </div>
    </div>
  )
}

export default CalculatorTool
